import { useId, useState } from "react";
import { StandardButton } from "@/components/common/StandardButton";

interface PasswordFieldProps {
  label: string;
  required?: boolean;
  className: string;
  onChange: (e: any) => void;
  placeholder: string;
  /** Error styling only. Prefer `errorMessage`, which also announces and associates. */
  hasError?: boolean;
  errorMessage?: string;
  inputRef?: React.Ref<HTMLInputElement>;
}

function PasswordField(props: PasswordFieldProps) {
  const {
    label,
    required = false,
    className,
    onChange,
    hasError = false,
    errorMessage,
    inputRef,
    ...otherProps
  } = props;

  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  // useId, not a literal: two fields on one page would collide and both labels
  // would resolve to the first input.
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const isInvalid = hasError || !!errorMessage;

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    onChange(e); // Call the onChange prop function to lift state up or handle changes
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <label htmlFor={inputId} className="flex items-center gap-1">
        <span className={`label-s-medium ${isInvalid ? "text-fg-error-strong" : "text-fg-neutral-strong"}`}>{label}</span>
        {required && <span className="text-red-600 label-s-medium" aria-hidden="true">*</span>}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type={showPassword ? "text" : "password"}
          id={inputId}
          required={required}
          aria-invalid={isInvalid ? "true" : "false"}
          aria-describedby={errorMessage ? errorId : undefined}
          className={`w-full p-2 rounded-lg text-black text-base md:text-[13px] placeholder:text-stone-300 border ${isInvalid ? "border-border-token-error-subtle bg-bg-error-tertiary-default" : "border-violet-400 bg-transparent"}`}
          onChange={handlePasswordChange}
          {...otherProps}
        />
        {password && (
          <StandardButton
            type="button"
            id="togglePasswordVisibility"
            onClick={togglePasswordVisibility}
            variant="icon"
            size="icon-xlg"
            className="absolute right-2 top-1/2 -translate-y-1/2"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {/* Replace with an appropriate eye icon */}
            {!showPassword ? (
              <img src="/images/Eye.svg" alt="" aria-hidden="true" />
            ) : (
              <img src="/images/nimbus_eye-off.svg" alt="" aria-hidden="true" />
            )}
          </StandardButton>
        )}
      </div>
      {errorMessage && (
        <p id={errorId} role="alert" className="label-s-medium text-fg-error-subtle">
          {errorMessage}
        </p>
      )}
    </div>
  );
}

export default PasswordField;
